// src/pages/Posts.js

import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { useNavigation } from "../contexts/NavigationContext"; // Import NavigationContext
import { fetchPosts } from "../services/postService";
import { formatDistance } from "date-fns";
import { sl } from "date-fns/locale";
import PostFormModal from "../components/PostFormModal";
import UserAvatar from "../components/UserAvatar";
import "../components/CommunityPosts.css"; // Import the shared CSS

function Posts() {
  const { user } = useAuth();
  const { isMobile } = useNavigation(); // Get mobile status
  const [posts, setPosts] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalPages: 1,
    totalPosts: 0
  });
  
  // Modal control
  const [showPostModal, setShowPostModal] = useState(false);
  const [editingPostId, setEditingPostId] = useState(null);

  useEffect(() => {
    loadPosts(pagination.currentPage);
  }, [user, pagination.currentPage]);

  const loadPosts = async (page = 1) => {
    if (!user?.community_id) {
      setIsLoading(false);
      return;
    }
    
    setIsLoading(true);
    try {
      const response = await fetchPosts(page, 10);
      
      if (response) {
        setPosts(response.posts);
        setPagination({
          currentPage: response.current_page,
          totalPages: response.pages,
          totalPosts: response.total
        });
      }
    } catch (err) {
      setError("Napaka pri nalaganju objav.");
    } finally {
      setIsLoading(false);
    }
  };

  // Function to handle page change
  const handlePageChange = (newPage) => {
    if (newPage > 0 && newPage <= pagination.totalPages) {
      setPagination(prev => ({...prev, currentPage: newPage}));
    }
  };
  
  // Functions to handle modal
  const openNewPostModal = () => {
    setEditingPostId(null);
    setShowPostModal(true);
  };
  
  const openEditPostModal = (postId) => {
    setEditingPostId(postId);
    setShowPostModal(true);
  };
  
  const closePostModal = () => {
    setShowPostModal(false);
    setEditingPostId(null);
  };
  
  // Function for when a post is saved
  const handlePostSaved = (savedPost) => {
    // If editing, replace the post in the array
    if (editingPostId) {
      setPosts(posts.map(post => 
        post.id === savedPost.id ? savedPost : post
      ));
    } else {
      // If new post, add it to the beginning of the array
      setPosts([savedPost, ...posts]);
    }
  };

  // Function to format date as "X time ago" in Slovenian
  const formatTimeAgo = (dateString) => {
    if (!dateString) return "";
    const date = new Date(dateString);
    return formatDistance(date, new Date(), { addSuffix: true, locale: sl });
  };

  // Function to truncate text
  const truncateText = (text, maxLength = 200) => {
    if (!text || text.length <= maxLength) return text;
    return text.substring(0, maxLength) + "...";
  };

  // Generate pagination UI
  const renderPagination = () => {
    if (pagination.totalPages <= 1) return null;
    
    const pages = [];
    const { currentPage, totalPages } = pagination;
    
    // Previous button
    pages.push(
      <li key="prev" className={`page-item ${currentPage === 1 ? 'disabled' : ''}`}>
        <button 
          className="page-link" 
          onClick={() => handlePageChange(currentPage - 1)}
          disabled={currentPage === 1}
        >
          &laquo;
        </button>
      </li>
    );
    
    // Page numbers
    for (let i = Math.max(1, currentPage - 2); i <= Math.min(totalPages, currentPage + 2); i++) {
      pages.push(
        <li key={i} className={`page-item ${i === currentPage ? 'active' : ''}`}>
          <button 
            className="page-link" 
            onClick={() => handlePageChange(i)}
          >
            {i}
          </button>
        </li>
      );
    }
    
    // Next button
    pages.push(
      <li key="next" className={`page-item ${currentPage === totalPages ? 'disabled' : ''}`}>
        <button 
          className="page-link" 
          onClick={() => handlePageChange(currentPage + 1)}
          disabled={currentPage === totalPages}
        >
          &raquo;
        </button>
      </li>
    );
    
    return (
      <nav aria-label="Page navigation">
        <ul className="pagination justify-content-center">
          {pages}
        </ul>
      </nav>
    );
  };

  // Create actions dropdown for a post
  const renderPostActions = (post) => {
    if (post.user_id === user?.id || user?.role === 'community_admin' || user?.role === 'global_admin') {
      return (
        <div className="dropdown">
          <button
            className="btn btn-sm btn-link text-muted"
            type="button"
            data-bs-toggle="dropdown"
            aria-expanded="false"
          >
            <i className="bi bi-three-dots-vertical"></i>
          </button>
          <ul className="dropdown-menu dropdown-menu-end">
            <li>
              <button 
                className="dropdown-item"
                onClick={() => openEditPostModal(post.id)}
              >
                <i className="bi bi-pencil me-2"></i> Uredi
              </button>
            </li>
          </ul>
        </div>
      );
    }
    return null;
  };

  return (
    <div className="container py-4">
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h1 className={isMobile ? "fs-3" : ""}>Objave skupnosti</h1>
        <button className="btn btn-primary" onClick={openNewPostModal}>
          <i className="bi bi-plus-circle me-2"></i> Nova objava
        </button>
      </div>
      
      {isLoading ? (
        <div className="text-center py-5">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
        </div>
      ) : error ? (
        <div className="alert alert-danger" role="alert">
          {error}
        </div>
      ) : !user?.community_id ? (
        <div className="alert alert-info" role="alert">
          <p>Za ogled objav se najprej pridruži skupnosti.</p>
        </div>
      ) : posts.length === 0 ? (
        <div className="text-center py-5">
          <p className="lead text-muted mb-4">Ni objav v tvoji skupnosti.</p>
          <button className="btn btn-lg btn-primary" onClick={openNewPostModal}>
            Ustvari prvo objavo
          </button>
        </div>
      ) : (
        <>
          <div className="row">
            <div className="col-md-12">
              {posts.map(post => (
                <div key={post.id} className="card shadow-sm border-0 rounded-4 mb-4">
                  <div className="card-body p-4">
                    {isMobile ? (
                      // Mobile-friendly chat-like display
                      <div>
                                                  <div className="d-flex align-items-center mb-2">
                          {/* Avatar and name in compact format */}
                          <UserAvatar
                            profileImage={post.profile_image}
                            username={post.username}
                            size={32}
                            className="me-2"
                            avatarOnly={true} // Only show the avatar
                          />
                          <div className="d-flex justify-content-between align-items-baseline flex-grow-1">
                            <div>
                              <span className="fw-semibold text-truncate d-inline-block" style={{maxWidth: '150px'}}>
                                {post.display_name || post.username}
                              </span>
                              <div className="text-muted x-small">
                                {formatTimeAgo(post.created_at)}
                              </div>
                            </div>
                            {renderPostActions(post)}
                          </div>
                        </div>
                        
                        {post.title && <h5 className="mt-2 mb-2">{post.title}</h5>}
                        
                        <p className="mb-3">{truncateText(post.content, isMobile ? 150 : 200)}</p>
                        
                        <div className="d-flex justify-content-between align-items-center">
                          <Link to={`/posts/${post.id}`} className="btn btn-sm btn-outline-primary">
                            Preberi več
                          </Link>
                          <span className="text-muted small">
                            <i className="bi bi-chat-text me-1"></i> 
                            {post.comments_count || 0}
                          </span>
                        </div>
                      </div>
                    ) : (
                      // Desktop version
                      <div className="d-flex align-items-start">
                        <div className="flex-grow-1">
                          {/* Use UserAvatar component with actions */}
                          <UserAvatar
                            profileImage={post.profile_image}
                            username={post.username}
                            displayName={post.display_name}
                            timestamp={post.created_at}
                            timestampFormat={formatTimeAgo(post.created_at)}
                            size={48}
                            actions={renderPostActions(post)}
                          />
                          
                          {post.title && <h4 className="mt-3 mb-2">{post.title}</h4>}
                          
                          <p className="mb-3">{truncateText(post.content)}</p>
                          
                          <div className="d-flex justify-content-between align-items-center">
                            <Link to={`/posts/${post.id}`} className="btn btn-sm btn-outline-primary">
                              Preberi več
                            </Link>
                            <span className="text-muted">
                              <i className="bi bi-chat-text me-1"></i> 
                              {post.comments_count || 0} komentarjev
                            </span>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
          
          {renderPagination()}
        </>
      )}
      
      {/* Post Form Modal */}
      <PostFormModal 
        show={showPostModal}
        handleClose={closePostModal}
        postId={editingPostId}
        onPostSaved={handlePostSaved}
      />
    </div>
  );
}

export default Posts;